import { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useGettingStartedSideBar } from '@hotjar/webapp/features/Activation/GetStartedGuide/hooks/useGettingStartedSideBar';
import { GettingStartedGuide } from './GettingStartedGuide';

type WithGettingStartedSideBarProps = {
  children: JSX.Element;
};

export const GSG_BAR_WIDTH = 280;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  overflow: hidden;
`;

const InnerContent = styled.div<{ isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  transition: width 200ms ease-in-out;

  @media (min-width: 1400px) {
    ${({ isOpen }) => (isOpen ? `width: calc(100% - ${GSG_BAR_WIDTH}px);` : `width: 100%;`)}
  }
`;

const GSGSideBar = styled.div<{ topDistance?: number; isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 0 0 ${GSG_BAR_WIDTH}px;
  background-color: ${tokens.color.bg_info};
  padding: ${tokens.spacer.x2} ${tokens.spacer.x1pt5};
  border: 1px solid ${tokens.color.ui_border};
  ${tokens.elevation(1)};
  position: fixed;
  ${({ isOpen }) => (isOpen ? `right: ${tokens.spacer.x1};` : `right: -${GSG_BAR_WIDTH}px;`)}
  min-height: 200px;
  border-radius: ${tokens.radius.l};
  max-width: ${GSG_BAR_WIDTH}px;
  min-width: ${GSG_BAR_WIDTH}px;
  overflow: hidden;
  transition: right 200ms ease-in-out;

  &:before {
    content: '';
    display: block;
    position: absolute;
    width: 330px;
    height: 330px;
    right: -179px;
    top: -12px;

    background: radial-gradient(
      105% 117.79% at 48.77% 80.34%,
      rgba(161, 143, 214, 0.3) 8.43%,
      rgba(195, 240, 247, 0.3) 58.85%,
      rgba(195, 240, 247, 0) 100%
    );
    mix-blend-mode: darken;
    filter: blur(50px);
    transform: rotate(-30deg);
  }

  @media (min-width: 1400px) {
    padding: ${tokens.spacer.x2} ${tokens.spacer.x1pt5};
    height: 100vh;
    box-shadow: unset;
    border: unset;
    border-radius: unset;
    border-left: 1px solid ${tokens.color.ui_border};
    ${({ isOpen }) => (isOpen ? `right: 0;` : `right: -${GSG_BAR_WIDTH}px;`)}
  }
`;

export const WithGettingStartedSideBar = ({
  children,
}: WithGettingStartedSideBarProps): JSX.Element => {
  const { isSideBarEnabled, isOpen } = useGettingStartedSideBar();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sideBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!wrapperRef?.current || !sideBarRef?.current) {
      return () => null;
    }
    // Sync sizes when the table size changes (added/removed columns)
    const syncSideBarHeight = () => {
      if (!wrapperRef.current || !sideBarRef.current) {
        return;
      }

      sideBarRef.current.style.top = `${wrapperRef.current.getBoundingClientRect().y}px`;
    };

    const wrapperHeightObserver = new ResizeObserver(() => {
      syncSideBarHeight();
    });
    wrapperHeightObserver.observe(wrapperRef.current);

    return () => {
      wrapperHeightObserver.disconnect();
    };
  }, [wrapperRef, sideBarRef]);

  if (!isSideBarEnabled) {
    return <>{children}</>;
  }

  return (
    <Wrapper ref={wrapperRef}>
      <InnerContent isOpen={isOpen}>{children}</InnerContent>
      <GSGSideBar ref={sideBarRef} isOpen={isOpen} data-testid="gsg-sidebar">
        <GettingStartedGuide inSideBar />
      </GSGSideBar>
    </Wrapper>
  );
};
